import { Form, Input, Modal, Radio, Space, Typography } from 'antd';
import { type FC, useCallback, useContext, useMemo, useState } from 'react';

import { MangoAtcTokenAdditionalComponents } from '@app//pages/Integrations/IntegrationsList/components/MangoAtcTokenAdditionalComponents';
import {
  ATC_BEELINE,
  ATC_MANGO,
  CANCEL,
  ERROR_REQUIRED_FIELD,
  MAX_LENGTH_MESSAGE,
  PREFIX,
  RULES_IS_REQUIRE,
  SAVE,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { AtcPhoneNumbersBlock } from '@app/pages/Integrations/IntegrationsList/components/AtcPhoneNumbersBlock';
import { SalesFunnelFormItems } from '@app/pages/Integrations/IntegrationsList/components/SalesFunnelsFormItems';
import {
  type AddAtcModalForm,
  type AddAtcModalProps,
  PhoneModes,
} from '@app/pages/Integrations/IntegrationsList/types';
import { IntegrationsContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import {
  useAddAtcBeelineIntegrationMutation,
  useAddAtcMangoIntegrationMutation,
} from '@app/services/rtkQuery/SocialIntegrations/api';

const { Text } = Typography;

const ATC_BEELINE_TITLE = 'Добавить Beeline интеграцию';
const ATC_MANGO_TITLE = 'Добавить интеграцию Mango telecom';

export const AddAtcModal: FC<AddAtcModalProps> = ({
  setInitialSalesFunnel,
  setSelectedSalesFunnelId,
  salesFunnels,
  salesFunnelsStages,
  getData,
  editableIntegration,
  token,
  setToken,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const [
    addAtcBeelineIntegration,
    { isLoading: isAddAtcBeelineIntegrationLoading },
  ] = useAddAtcBeelineIntegrationMutation();
  const [
    addAtcMangoIntegration,
    { isLoading: isAddAtcMangoIntegrationLoading },
  ] = useAddAtcMangoIntegrationMutation();

  const integrationSubmitHandlers = useMemo(
    () => ({
      [ATC_BEELINE]: addAtcBeelineIntegration,
      [ATC_MANGO]: addAtcMangoIntegration,
    }),
    [addAtcBeelineIntegration, addAtcMangoIntegration]
  );

  const { isAtcModalOpen, setIsAtcModalOpen, setCurrentIntegrationType } =
    useContext(IntegrationsContext);

  const currentIntegrationType = useContext(IntegrationsContext)
    .currentIntegrationType as keyof typeof integrationSubmitHandlers;
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId } = useUserPrint();
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<
    string[] | null
  >(null);

  const resetModalState = useCallback(() => {
    setCurrentIntegrationType('');
    setIsAtcModalOpen(false);
    setToken(null);
    setSelectedPhoneNumbers(null);
    form.resetFields();
  }, [form, setCurrentIntegrationType, setIsAtcModalOpen, setToken]);

  const handleOk = () => {
    form.submit();
  };

  const handleSubmitForm = useCallback(
    async (values: AddAtcModalForm) => {
      const {
        Name,
        HistoryUploadPeriod,
        salesFunnel,
        salesFunnelStage,
        phoneNumbersSelector,
      } = values;

      setIsSaving(true);
      try {
        const data = {
          ActiveBranchId: activeBranchId,
          Name,
          SalesFunnelId: salesFunnel,
          SalesFunnelStageId: salesFunnelStage,
          HistoryUploadPeriod,
          Token: token!,
          Abonents:
            phoneNumbersSelector === PhoneModes.ALL ||
            selectedPhoneNumbers === null
              ? []
              : selectedPhoneNumbers,
        };

        const submitHandler =
          integrationSubmitHandlers[
            currentIntegrationType as keyof typeof integrationSubmitHandlers
          ];

        if (!submitHandler) {
          throw new Error('Неизвестный тип интеграции');
        }

        await submitHandler(data).unwrap();

        resetModalState();
        showNotification('Интеграция создана успешно', SUCCESS);

        await getData();
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setIsSaving(false);
      }
    },
    [
      activeBranchId,
      currentIntegrationType,
      integrationSubmitHandlers,
      selectedPhoneNumbers,
      token,
      getData,
      showNotification,
      resetModalState,
    ]
  );

  const handleSalesFunnelChange = (value: number) => {
    setSelectedSalesFunnelId(value);
    form.setFieldsValue({
      salesFunnelStage: null,
    });
  };

  const getModalTitle = () => {
    switch (currentIntegrationType) {
      case ATC_BEELINE:
        return ATC_BEELINE_TITLE;
      case ATC_MANGO:
        return ATC_MANGO_TITLE;
      default:
        return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      className={`${PREFIX}add-atc-modal`}
      open={isAtcModalOpen}
      onCancel={resetModalState}
      onOk={handleOk}
      cancelText={CANCEL}
      okText={SAVE}
      okButtonProps={{
        loading:
          isAddAtcBeelineIntegrationLoading || isAddAtcMangoIntegrationLoading,
      }}
      cancelButtonProps={{
        disabled:
          isAddAtcBeelineIntegrationLoading || isAddAtcMangoIntegrationLoading,
      }}
      destroyOnHidden
    >
      <Form
        form={form}
        onFinish={handleSubmitForm}
        initialValues={{
          HistoryUploadPeriod: 4,
          salesFunnel: setInitialSalesFunnel(),
          VirtualAtcApiAddress: 'https://app.mango-office.ru/vpbx/',
        }}
        disabled={isSaving}
      >
        <div className="container">
          <div className="item-container">
            <Text strong className="label label-require">
              Название интеграции
            </Text>
            <Form.Item
              name="Name"
              rules={[
                {
                  max: 140,
                  message: MAX_LENGTH_MESSAGE,
                },
                ...RULES_IS_REQUIRE,
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </div>
          <div className="item-container">
            <Text strong className="label">
              Загрузка сообщений чата
            </Text>
            <Form.Item name="HistoryUploadPeriod">
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value={1}>Все</Radio>
                  <Radio value={2}>За неделю</Radio>
                  <Radio value={3}>Сегодняшние</Radio>
                  <Radio value={4}>Не загружать</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </div>
        </div>
        <SalesFunnelFormItems
          salesFunnels={salesFunnels}
          salesFunnelsStages={salesFunnelsStages}
          onSalesFunnelChange={handleSalesFunnelChange}
          errorRequiredField={ERROR_REQUIRED_FIELD}
          isIntegrationRegistryComponent={true}
        />
        {currentIntegrationType === ATC_BEELINE && (
          <div className="item-container">
            <Text strong className="label label-require">
              Введите токен авторизации
            </Text>
            <Form.Item name="Token" rules={[...RULES_IS_REQUIRE]}>
              <Input size="large" onChange={(e) => setToken(e.target.value)} />
            </Form.Item>
          </div>
        )}
        {currentIntegrationType === ATC_MANGO && (
          <MangoAtcTokenAdditionalComponents form={form} setToken={setToken} />
        )}
        {token ? (
          <AtcPhoneNumbersBlock
            activeBranchId={activeBranchId}
            atcToken={token}
            setSelectedPhoneNumbers={setSelectedPhoneNumbers}
            selectedPhoneNumbers={selectedPhoneNumbers}
            form={form}
            editableIntegration={editableIntegration}
            integrationSocialType={currentIntegrationType}
            disabled={isSaving}
            isOpen={isAtcModalOpen}
          />
        ) : null}
      </Form>
    </Modal>
  );
};
